// Sidenav Convokko: navegación principal sobre azul marino de marca,
// con item activo en píldora naranja y tarjeta de usuario con cierre de sesión.
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import { jwtDecode } from "jwt-decode";

import List from "@mui/material/List";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import SportsIcon from "@mui/icons-material/Sports";

import SidenavRoot from "examples/Sidenav/SidenavRoot";
import { useMaterialUIController, setMiniSidenav } from "context";
import { useSeason } from "context/SeasonContext";

const ROL_LABELS = {
  coordinador: "Coordinador",
  entrenador: "Entrenador",
};

function NavItem({ icon, name, active, mini }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mx: 2,
        my: 0.35,
        px: mini ? 0 : 1.75,
        py: 1.1,
        borderRadius: "12px",
        justifyContent: mini ? "center" : "flex-start",
        color: active ? "#fff" : "rgba(255,255,255,0.72)",
        backgroundColor: active ? "#F59E0B" : "transparent",
        transition: "background .15s ease, color .15s ease",
        "&:hover": {
          backgroundColor: active ? "#F59E0B" : "rgba(255,255,255,0.08)",
          color: "#fff",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          minWidth: 24,
          "& .MuiSvgIcon-root": { fontSize: 20 },
        }}
      >
        {typeof icon === "string" ? <Icon fontSize="small">{icon}</Icon> : icon}
      </Box>
      {!mini && (
        <Typography
          variant="button"
          sx={{
            fontWeight: active ? 700 : 500,
            fontSize: "0.85rem",
            textTransform: "none",
            color: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
      )}
    </Box>
  );
}

NavItem.propTypes = {
  icon: PropTypes.node,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  mini: PropTypes.bool,
};

function Sidenav({ brand, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const { temporada } = useSeason();
  const collapseName = location.pathname.replace("/", "");

  const rol = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      return token ? jwtDecode(token).rol : null;
    } catch (e) {
      return null;
    }
  }, []);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // El cierre de sesión vive en la tarjeta de usuario, no en la lista de navegación
  const navRoutes = routes.filter((r) => r.type === "collapse" && r.key !== "sign-out");

  return (
    <>
      {/* Backdrop en móvil: cierra el menú al tocar fuera */}
      {!miniSidenav && (
        <Box
          onClick={closeSidenav}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1150,
            bgcolor: "rgba(15,22,48,0.45)",
            display: { xs: "block", xl: "none" },
          }}
        />
      )}
      <SidenavRoot {...rest} variant="permanent" ownerState={{ miniSidenav }}>
        {/* Cabecera con logo */}
        <Box pt={2.5} pb={1.5} px={3} textAlign="center" position="relative">
          <Box
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={10}
            right={10}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <CloseIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }} />
          </Box>
          <Box
            component={NavLink}
            to="/"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            {brand && (
              <Box
                component="img"
                src={brand}
                alt="Convokko"
                sx={{
                  width: miniSidenav ? "2.4rem" : "72%",
                  maxWidth: "150px",
                  height: "auto",
                  filter: "brightness(0) invert(1)",
                  transition: "width 0.2s ease",
                }}
              />
            )}
          </Box>
        </Box>

        <Box mx={2.5} mb={1} sx={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }} />

        {!miniSidenav && (
          <Typography
            variant="caption"
            sx={{
              px: 4,
              pt: 1,
              pb: 0.5,
              display: "block",
              color: "rgba(255,255,255,0.45)",
              fontWeight: 700,
              letterSpacing: "0.08em",
              fontSize: "0.65rem",
            }}
          >
            GESTIÓN
          </Typography>
        )}

        <List sx={{ pt: 0.5 }}>
          {navRoutes.map(({ name, icon, key, route, external }) =>
            external ? (
              <a
                key={key}
                href={route}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <NavItem icon={icon} name={name} active={false} mini={miniSidenav} />
              </a>
            ) : (
              <NavLink key={key} to={route} style={{ textDecoration: "none" }}>
                <NavItem icon={icon} name={name} active={key === collapseName} mini={miniSidenav} />
              </NavLink>
            )
          )}
        </List>

        {/* Tarjeta de usuario + logout */}
        <Box mt="auto" p={2}>
          <Box
            sx={{
              borderRadius: "16px",
              bgcolor: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.10)",
              p: miniSidenav ? 1 : 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              justifyContent: miniSidenav ? "center" : "flex-start",
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#F59E0B",
              }}
            >
              <SportsIcon sx={{ fontSize: 20, color: "#fff" }} />
            </Avatar>
            {!miniSidenav && (
              <>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#fff", fontWeight: 700, display: "block", lineHeight: 1.3 }}
                    noWrap
                  >
                    {ROL_LABELS[rol] || "Usuario"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.68rem" }}
                    noWrap
                    display="block"
                  >
                    Temporada {temporada}/{String(Number(temporada) + 1).slice(-2)}
                  </Typography>
                </Box>
                <Tooltip title="Cerrar sesión">
                  <IconButton
                    size="small"
                    onClick={() => navigate("/logout")}
                    sx={{
                      color: "rgba(255,255,255,0.65)",
                      "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" },
                    }}
                  >
                    <LogoutIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </SidenavRoot>
    </>
  );
}

Sidenav.defaultProps = {
  brand: "",
  brandName: "",
  color: "info",
};

Sidenav.propTypes = {
  color: PropTypes.string,
  brand: PropTypes.string,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
